import React from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Grid } from '@atlaskit/primitives/grid';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss';

import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { EndOfPageCard } from '@confluence/page-card/entry-points/EndOfPageCard.ts';
import type { GlobalPageCard } from '@confluence/page-card/entry-points/cardTypes.ts';

import { ThirdPartyPromptSlot } from './ThirdPartyPromptSlot.tsx';
import { useThirdPartyPromptPlacement } from './useThirdPartyPromptPlacement.ts';

type EndOfPageRecGridWithThirdPartyPromptProps = {
	pageCardData: GlobalPageCard[];
	pageRecStrategy?: string[][];
	entityId: string;
	isCompact?: boolean;
};

const GRID_CARD_LIMIT = 6;
const LIVE_PAGE_GRID_CARD_LIMIT = 4;

const TwoCardWrapperWidth = xcss({
	maxWidth: '480px',
});

const ThreeCardWrapperWidth = xcss({
	maxWidth: '320px',
});

const BigCardHeight = xcss({
	height: '128px',
});

const getColumnsBasedOnNumberOfCards = (totalCards: number) => {
	const fixedTwoColumns = '1fr 1fr';
	const autoFitColumns = 'repeat(auto-fit, minmax(320px, 1fr))';
	switch (totalCards) {
		case 1:
		case 2:
			return fixedTwoColumns;
		default:
			return autoFitColumns;
	}
};

const getCardStyles = (totalCards: number) => {
	let stylesArray;
	switch (totalCards) {
		case 1:
		case 2:
		case 4:
			stylesArray = [TwoCardWrapperWidth];
			break;
		default:
			stylesArray = [ThreeCardWrapperWidth];
			break;
	}
	stylesArray.push(BigCardHeight);
	return stylesArray;
};

export const EndOfPageRecGridWithThirdPartyPrompt = ({
	pageCardData,
	pageRecStrategy,
	entityId,
	isCompact,
}: EndOfPageRecGridWithThirdPartyPromptProps): React.JSX.Element | null => {
	const isLivePage = useIsCurrentPageLive();
	const cardLimit = isLivePage ? LIVE_PAGE_GRID_CARD_LIMIT : GRID_CARD_LIMIT;
	const { placement, product, isResolved } = useThirdPartyPromptPlacement(entityId);

	if (placement === 'holding') {
		// Recs and the connector slot must appear in the same paint.
		return null;
	}

	const shouldRestoreDroppedRec = placement === 'first' && isResolved && !product;
	const shouldDropLastRec =
		placement === 'first' && (pageCardData.length >= cardLimit || shouldRestoreDroppedRec);
	const recs = shouldDropLastRec ? pageCardData.slice(0, -1) : pageCardData;
	const droppedRec = shouldDropLastRec ? pageCardData[pageCardData.length - 1] : undefined;
	const totalCards =
		placement === 'first' ? Math.min(pageCardData.length + 1, cardLimit) : pageCardData.length;

	return (
		<Grid templateColumns={getColumnsBasedOnNumberOfCards(totalCards)} gap="space.200" role="list">
			{placement === 'first' ? (
				<Box
					key="third-party-related-content-prompt"
					xcss={() => getCardStyles(totalCards)}
					role="listitem"
				>
					<ThirdPartyPromptSlot product={product} isResolved={isResolved}>
						{droppedRec ? (
							<EndOfPageCard
								data={droppedRec}
								ranking={pageCardData.length - 1}
								analyticsData={{
									source: 'endOfPageRecommendations',
									attributes: {
										strategy: pageRecStrategy
											? pageRecStrategy[(pageCardData.length - 1) % pageRecStrategy.length]
											: null,
										mainContentId: entityId,
									},
								}}
								isCompactView={isCompact}
								showCardsAsListitem={false}
							/>
						) : null}
					</ThirdPartyPromptSlot>
				</Box>
			) : null}
			{recs.map((node, i) => (
				// eslint-disable-next-line @atlassian/perf-linting/detect-unnecessary-rerenders -- Existing callback moved with the component.
				<Box key={node.id} xcss={() => getCardStyles(totalCards)} role="listitem">
					<EndOfPageCard
						data={node}
						ranking={i}
						analyticsData={{
							source: 'endOfPageRecommendations',
							attributes: {
								strategy: pageRecStrategy ? pageRecStrategy[i % pageRecStrategy.length] : null,
								mainContentId: entityId,
							},
						}}
						isCompactView={isCompact}
						showCardsAsListitem={false}
					/>
				</Box>
			))}
		</Grid>
	);
};
